import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Mail } from "lucide-react";
import { api, formatApiErrorDetail, fmtDate } from "../lib/api";
import { IMG, UI, LANG } from "../data/content";

export const Reveal = ({ children, delay = 0, className = "", ...rest }) => {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduce ? false : { opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.45, delay, ease: "easeOut" }}
      {...rest}
    >
      {children}
    </motion.div>
  );
};

export const usePageMeta = (title, description) => {
  useEffect(() => {
    document.title = title;
    const m = document.querySelector('meta[name="description"]');
    if (m && description) m.setAttribute("content", description);
    window.scrollTo(0, 0);
  }, [title, description]);
};

export const CTAButton = ({ to, children, variant = "primary", testId }) => (
  <Link to={to} className={`btn-${variant}`} data-testid={testId}>
    {children} <ArrowRight size={16} className="arr" />
  </Link>
);

export const ArrowLink = ({ to, children, testId }) => (
  <Link to={to} className="arrow-link" data-testid={testId}>
    {children} <ArrowRight size={15} className="arr" />
  </Link>
);

export const HeroBand = ({ image, eyebrow, title, standfirst, cta, ctaTo, compact = false, testId }) => (
  <header className={`hero-band ${compact ? "hero-band--compact" : ""}`} data-testid={testId || "page-hero"}>
    <img src={IMG[image] || IMG.facade} alt="" className="hero-img" />
    <div className="hero-scrim" />
    <div className="k-container hero-content w-full py-16">
      <Reveal>
        {eyebrow && <p className="eyebrow mb-4">{eyebrow}</p>}
        <h1 className="k-h1 max-w-3xl">{title}</h1>
        {standfirst && <p className="standfirst max-w-2xl mt-5">{standfirst}</p>}
        {cta && (
          <div className="mt-8">
            <CTAButton to={ctaTo} variant="inverse" testId="hero-cta-btn">{cta}</CTAButton>
          </div>
        )}
      </Reveal>
    </div>
  </header>
);

export const SectionHead = ({ eyebrow, title, lead }) => (
  <Reveal className="mb-10">
    {eyebrow && <p className="eyebrow mb-3">{eyebrow}</p>}
    <h2 className="k-h2">{title}</h2>
    <div className="section-rule" />
    {lead && <p className="text-ink-muted mt-5 max-w-3xl">{lead}</p>}
  </Reveal>
);

export const CapCard = ({ to, title, body, i = 0, testId }) => (
  <Reveal delay={i * 0.06} className="h-full">
    <Link to={to} className="cap-card" data-testid={testId}>
      <h3 className="k-h3 cap-title mb-4">{title}</h3>
      <p className="text-ink-muted text-sm leading-relaxed mb-8 flex-1">{body}</p>
      <span className="arrow-link">{UI.common.readMore} <ArrowRight size={15} className="arr" /></span>
    </Link>
  </Reveal>
);

export const ContactModule = ({ heading = UI.common.cmHeading }) => (
  <Reveal>
    <div className="contact-module max-w-xl !items-center justify-between flex-wrap gap-6" data-testid="contact-module">
      <div>
        <p className="eyebrow mb-2">{heading}</p>
        <p className="text-sm text-ink-muted mb-3 max-w-sm">{UI.common.cmBody}</p>
        <a href="mailto:advisory@kairostrade.hk" className="flex items-center gap-2 text-brand no-underline text-sm font-semibold" data-testid="contact-module-email">
          <Mail size={14} /> advisory@kairostrade.hk
        </a>
      </div>
      <Link to="/contact-us" className="btn-primary shrink-0" data-testid="contact-module-btn">
        {UI.nav.contactUs} <ArrowRight size={16} className="arr" />
      </Link>
    </div>
  </Reveal>
);

export const ScopeBlock = ({ text }) => (
  <div className="scope-band" data-testid="scope-of-engagement">
    <h3 className="k-h3 mb-3">{UI.common.scope}</h3>
    <p className="text-ink-muted leading-relaxed text-[0.95rem] max-w-3xl">{text}</p>
  </div>
);

export const RelatedLinks = ({ items }) => (
  <div className="flex flex-wrap items-center gap-3" data-testid="related-links">
    <span className="text-sm font-semibold text-ink">{UI.common.related}</span>
    {items.map(([label, to]) => (
      <Link key={to} to={to} className="topic-tag !py-2 !px-4 no-underline" data-testid={`related-${to.split("/").pop()}`}>{label}</Link>
    ))}
  </div>
);

export const ArticleCard = ({ a, featured = false }) => (
  <Link to={`/insights/${a.slug}`} className="article-card" data-testid={`article-card-${a.slug}`}>
    <div className="flex items-center gap-4 mb-3">
      <span className="topic-tag">{a.topic.replace(/-/g, " ")}</span>
      <span className="text-ink-muted text-xs" style={{ fontVariantNumeric: "tabular-nums" }}>
        {fmtDate(a.publishedAt)} · {a.readingTime} {UI.common.minRead}
      </span>
    </div>
    <h3 className={`a-title font-semibold ${featured ? "text-2xl md:text-3xl" : "text-lg"} leading-snug mb-2`}>{a.title}</h3>
    <p className="text-ink-muted text-sm leading-relaxed max-w-2xl">{a.standfirst}</p>
  </Link>
);

export const SubscribeForm = ({ compact = false }) => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState({ status: "idle", msg: "" });
  const submit = async (e) => {
    e.preventDefault();
    setState({ status: "loading", msg: "" });
    try {
      const { data } = await api.post("/subscribe", { email, sourceUrl: window.location.href, lang: LANG });
      setState({ status: "done", msg: data.message });
      setEmail("");
    } catch (err) {
      setState({ status: "error", msg: formatApiErrorDetail(err.response?.data?.detail) });
    }
  };
  return (
    <div data-testid="subscribe-form">
      {!compact && (
        <p className="text-ink-muted text-sm mb-4 max-w-md">{UI.common.subBody}</p>
      )}
      <form onSubmit={submit} className="flex gap-3 max-w-md">
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder={UI.common.emailPh} className="field-input" data-testid="subscribe-email-input" />
        <button type="submit" className="btn-primary whitespace-nowrap" disabled={state.status === "loading"} data-testid="subscribe-submit-btn">
          {state.status === "loading" ? UI.common.sending : UI.common.subBtn}
        </button>
      </form>
      {state.msg && (
        <p className={`text-sm mt-3 ${state.status === "error" ? "text-red-600" : "text-brand"}`} data-testid="subscribe-message">{state.msg}</p>
      )}
    </div>
  );
};

export const useLatestArticles = (limit = 4) => {
  const [articles, setArticles] = useState([]);
  useEffect(() => {
    api.get(`/articles?limit=${limit}`).then(({ data }) => setArticles(data.items)).catch(() => {});
  }, [limit]);
  return articles;
};
